/**
 * Financial scenarios (FA pack — FA7; A9 hardening 2026-08-19).
 *
 * Pure helpers for multi-scenario management. A FinancialAssumptions
 * carries an optional `scenarios?: FinancialScenario[]`; each scenario
 * expresses its difference from the base case through TWO channels:
 *
 *   1. `assumptions` — ABSOLUTE overrides (`Partial<FinancialAssumptionsCore>`).
 *      "CAPEX is exactly $2B." Hand-authored / legacy. Applied first.
 *   2. `adjustments` — RELATIVE adjustments (`ScenarioAdjustments`), applied
 *      AFTER the absolute spread and RE-DERIVED against the current base on
 *      every merge. "−10 % CAPEX", "+5 pts CF", "−1 pt WACC".
 *
 * Why two channels (A9). The seeded Upside/Downside used to capture
 * `base.capexTotalUsd * 0.9` AT SEED TIME and store the resulting NUMBER.
 * When the base CAPEX later moved from $1.625B to $3.25B the "Upside"
 * scenario still carried $1.4625B — 45 % of base, not 90 % — and if that
 * scenario was canonical the whole report verdict ran on it. Relative
 * adjustments re-derive at merge time, so the seeded Upside is still −10 %
 * after the base doubles.
 *
 * The driver:
 *
 *   1. Merges base × scenario (`assumptions` then `adjustments`) into an
 *      effective core.
 *   2. Runs `recompute()` on the merged core.
 *   3. Reads the scenario marked `isCanonical` (the LLM/report basis; the
 *      others appear in the side-by-side compare panel).
 *
 * Canonical resolution is EXPLICIT (A9). No `isCanonical` flag ⇒ the BASE
 * assumptions are the basis — never an arbitrary `scenarios[0]`, which used
 * to hand the report verdict to whatever happened to sit first in the array
 * (typically a stress case). Multiple flags are a data error: the first in
 * array order wins and `resolveCanonicalCore` returns a diagnostic saying so.
 *
 * Pure: no UI, no store. Scenario writes are committed via the
 * existing `updateSiteFinancialAssumptions` store action — UI passes
 * the new `scenarios` array on the FinancialAssumptions payload.
 *
 * Default scenarios (Base / Upside / Downside) are seeded via
 * `seedDefaultScenariosFor()` when a site has none and the user opens
 * the compare panel for the first time. The Base scenario carries an
 * empty diff (== canonical core).
 *
 * TYPE NOTE. `adjustments` is declared here as a structural extension
 * (`FinancialScenarioWithAdjustments`) rather than on `FinancialScenario`
 * in `src/types/financialAssumptions.ts`. The field is optional, so a
 * plain `FinancialScenario` is assignable to the extension and vice versa,
 * and the store persists the scenarios array verbatim (no field-level
 * sanitiser), so the extra key round-trips. The canonical home for the
 * field is the types file — see the report accompanying this change.
 */
import { convertCoreToUsd, recompute, type RecomputeResult } from './projectFinance';
import type {
  FinancialAssumptions,
  FinancialAssumptionsCore,
  FinancialScenario,
  ScenarioAdjustment,
  ScenarioAdjustmentKind,
  ScenarioAdjustableField,
  ScenarioAdjustments,
  FinancialPhase,
} from '../types/financialAssumptions';

/* ────────────────────────────────────────────────────────────────────────
 * Relative adjustments
 * ──────────────────────────────────────────────────────────────────── */

// The adjustment vocabulary lives with the schema it adjusts
// (`src/types/financialAssumptions.ts`); re-exported here so scenario callers
// have one import site.
export type {
  ScenarioAdjustment,
  ScenarioAdjustmentKind,
  ScenarioAdjustableField,
  ScenarioAdjustments,
};

/**
 * @deprecated `FinancialScenario` now carries `adjustments` itself — this alias
 * remains only so existing imports keep compiling. Prefer `FinancialScenario`.
 */
export type FinancialScenarioWithAdjustments = FinancialScenario;

export interface MergedScenario {
  scenario: FinancialScenarioWithAdjustments;
  /** Effective core after the scenario diff has been merged on top of the base. */
  effective: FinancialAssumptionsCore;
  /** Recomputed metrics for the effective core. */
  metrics: RecomputeResult;
}

interface FieldClamp {
  min?: number;
  max?: number;
}

/**
 * Physical guards applied to the RESULT of an adjustment (never to a
 * hand-authored `assumptions` override, whose literal value is the point).
 *
 * These are non-physical-value guards, not modelling assumptions:
 *  - `capacityFactorPct` [1, 100] and `waccPct` [1, 30] reproduce EXACTLY the
 *    clamps the seeded Upside/Downside diffs already applied.
 *  - `debtToEquityRatio` [0, 1] is the documented domain of the field.
 *  - `taxRatePct` [0, 100] is definitional.
 *  - the remaining entries only forbid a negative cost / capacity / life,
 *    which no cash-flow formula can interpret.
 * Fields absent from this table are unclamped.
 */
const ADJUSTMENT_CLAMPS: Partial<Record<ScenarioAdjustableField, FieldClamp>> = {
  capacityFactorPct: { min: 1, max: 100 },
  waccPct: { min: 1, max: 30 },
  debtToEquityRatio: { min: 0, max: 1 },
  taxRatePct: { min: 0, max: 100 },
  capacityMW: { min: 0 },
  projectLifeYears: { min: 1 },
  capexTotalUsd: { min: 0 },
  capexPerKwUsd: { min: 0 },
  constructionMonths: { min: 0 },
  decommissioningUsd: { min: 0 },
  workingCapitalUsd: { min: 0 },
  contingencyPct: { min: 0 },
  fixedOmPerKwYearUsd: { min: 0 },
  variableOmPerMwhUsd: { min: 0 },
  fuelCostPerMwhUsd: { min: 0 },
  insuranceAndPropertyTaxPct: { min: 0 },
  debtRatePct: { min: 0 },
  equityHurdlePct: { min: 0 },
  itcPct: { min: 0 },
  ptcPerMwhUsd: { min: 0 },
  ppaPricePerMwhUsd: { min: 0 },
  ppaTermYears: { min: 0 },
};

function clampAdjusted(field: ScenarioAdjustableField, value: number): number {
  const clamp = ADJUSTMENT_CLAMPS[field];
  if (!clamp) return value;
  let out = value;
  if (clamp.min != null) out = Math.max(clamp.min, out);
  if (clamp.max != null) out = Math.min(clamp.max, out);
  return out;
}

/** Read the (optional) relative-adjustment channel off a scenario. */
export function scenarioAdjustments(scenario: FinancialScenario): ScenarioAdjustments {
  const raw = (scenario as FinancialScenarioWithAdjustments).adjustments;
  return raw != null && typeof raw === 'object' ? raw : {};
}

function applyAdjustment(
  field: ScenarioAdjustableField,
  current: number,
  adjustment: ScenarioAdjustment,
): number {
  switch (adjustment.kind) {
    case 'multiplier':
      return clampAdjusted(field, current * adjustment.value);
    case 'delta':
      return clampAdjusted(field, current + adjustment.value);
    case 'absolute':
      return clampAdjusted(field, adjustment.value);
    default:
      return current;
  }
}

/**
 * Merge a scenario on top of the canonical core.
 * Returns a NEW core; does not mutate either input.
 *
 * Order: `base` → `scenario.assumptions` (absolute overrides, literal) →
 * `scenario.adjustments` (relative, re-derived and clamped).
 *
 * An adjustment is a NO-OP when the merged value for that field is absent
 * (`null` / non-finite): a multiplier on a field the model does not carry has
 * no meaning. Pinning a field the base lacks is what the absolute
 * `assumptions` channel is for.
 *
 * Empty / undefined `assumptions` + `adjustments` falls through to a clone of
 * `base`.
 */
export function mergeScenario(
  base: FinancialAssumptionsCore,
  scenario: FinancialScenario | FinancialScenarioWithAdjustments,
): FinancialAssumptionsCore {
  const merged: FinancialAssumptionsCore = { ...base, ...(scenario.assumptions ?? {}) };
  const adjustments = scenarioAdjustments(scenario);
  const numeric: Record<ScenarioAdjustableField, number | null> = merged;
  for (const field of Object.keys(adjustments) as ScenarioAdjustableField[]) {
    const adjustment = adjustments[field];
    if (!adjustment || !Number.isFinite(adjustment.value)) continue;
    const current = numeric[field];
    // No-op for fields absent from the base (A9).
    if (typeof current !== 'number' || !Number.isFinite(current)) continue;
    numeric[field] = applyAdjustment(field, current, adjustment);
  }
  return merged;
}

/** Merge ONE scenario and recompute its metrics. */
export function mergeScenarioWithMetrics(
  base: FinancialAssumptionsCore,
  scenario: FinancialScenario | FinancialScenarioWithAdjustments,
): MergedScenario {
  const effective = mergeScenario(base, scenario);
  return { scenario, effective, metrics: recompute(effective) };
}

/**
 * Merge every scenario on the FinancialAssumptions and run
 * `recompute()` on each effective core. Returns one MergedScenario
 * per scenario, in input order.
 *
 * When `fa.scenarios` is undefined or empty, returns an empty array.
 * The compare panel renders a "Seed Base/Upside/Downside" CTA in that
 * case.
 */
export function mergeAllScenarios(fa: FinancialAssumptions): MergedScenario[] {
  const scenarios = fa.scenarios ?? [];
  return scenarios.map((scenario) => mergeScenarioWithMetrics(fa, scenario));
}

/** Id of the synthetic row that stands for the UNMODIFIED base assumptions. */
export const BASE_SCENARIO_ID = '__base__';
export const BASE_SCENARIO_LABEL = 'Base (unmodified)';

/**
 * The synthetic "no scenario applied" row — the basis whenever no scenario
 * is marked canonical. Lets the compare panel measure deltas against the
 * real basis instead of promoting an arbitrary scenario.
 */
export function baseMergedScenario(fa: FinancialAssumptions): MergedScenario {
  return mergeScenarioWithMetrics(fa, {
    id: BASE_SCENARIO_ID,
    label: BASE_SCENARIO_LABEL,
    assumptions: {},
    isCanonical: false,
  });
}

/* ────────────────────────────────────────────────────────────────────────
 * Canonical resolution
 * ──────────────────────────────────────────────────────────────────── */

export type CanonicalDiagnosticCode = 'no-canonical' | 'multiple-canonical';

export interface CanonicalDiagnostic {
  code: CanonicalDiagnosticCode;
  /** Human-readable; safe to render in the compare panel. */
  message: string;
  /** Scenario ids involved — all scenarios for `no-canonical`, the flagged ones otherwise. */
  scenarioIds: string[];
}

export interface CanonicalResolution {
  /** The effective core the report / LLM must read. */
  core: FinancialAssumptionsCore;
  /** The scenario that produced `core`, or `null` when the BASE FA is the basis. */
  canonical: FinancialScenarioWithAdjustments | null;
  /** `null` when the record is well-formed. */
  diagnostic: CanonicalDiagnostic | null;
}

/**
 * Resolve the canonical basis EXPLICITLY, with a data-integrity diagnostic.
 *
 *   0 flagged ⇒ the base FA, unchanged (`canonical: null`). Reports must
 *               never silently run on `scenarios[0]` (A9).
 *   1 flagged ⇒ the merged core.
 *   n flagged ⇒ the first in array order wins, plus a `multiple-canonical`
 *               diagnostic. Pure: the caller decides how to surface it.
 */
export function resolveCanonicalCore(fa: FinancialAssumptions): CanonicalResolution {
  if (fa.businessModelKind === 'data-center-capacity-fee') {
    if (fa.scenarios?.length) throw new Error('Generation/PPA scenarios cannot override annual data-center inputs.');
    return { core: fa, canonical: null, diagnostic: null };
  }
  const scenarios = fa.scenarios ?? [];
  const flagged = scenarios.filter(s => s.isCanonical);

  if (flagged.length === 0) {
    return {
      // Even with no scenario, the core must reach the engine in USD.
      core: convertCoreToUsd(fa, fa.exchangeRateToUsd),
      canonical: null,
      diagnostic: scenarios.length === 0
        ? null
        : {
          code: 'no-canonical',
          message:
            `${scenarios.length} scenario${scenarios.length === 1 ? '' : 's'} defined but none is marked `
            + 'canonical — reports and the LLM read the unmodified base assumptions. Mark one with ★.',
          scenarioIds: scenarios.map(s => s.id),
        },
    };
  }

  const canonical = flagged[0];
  return {
    core: convertCoreToUsd(mergeScenario(fa, canonical), fa.exchangeRateToUsd),
    canonical,
    diagnostic: flagged.length === 1
      ? null
      : {
        code: 'multiple-canonical',
        message:
          `${flagged.length} scenarios are marked canonical (${flagged.map(s => s.label).join(', ')}) — `
          + `"${canonical.label}" wins by array order. Mark exactly one.`,
        scenarioIds: flagged.map(s => s.id),
      },
  };
}

/**
 * Per-phase fields a scenario adjustment propagates to, and the campus field
 * whose adjustment drives each one. A scenario says "CAPEX −10%" about the
 * PROJECT; the phases are that project's parts, so they scale with it.
 */
type AdjustablePhaseField =
  | 'capexTotalUsd'
  | 'capexPerKwUsd'
  | 'capacityMW'
  | 'capacityFactorPct'
  | 'fixedOmPerKwYearUsd'
  | 'variableOmPerMwhUsd'
  | 'fuelCostPerMwhUsd';

const PHASE_FIELD_FROM_CAMPUS: ReadonlyArray<
  readonly [phaseField: AdjustablePhaseField, campusField: ScenarioAdjustableField]
> = [
  ['capexTotalUsd', 'capexTotalUsd'],
  ['capexPerKwUsd', 'capexPerKwUsd'],
  ['capacityMW', 'capacityMW'],
  ['capacityFactorPct', 'capacityFactorPct'],
  ['fixedOmPerKwYearUsd', 'fixedOmPerKwYearUsd'],
  ['variableOmPerMwhUsd', 'variableOmPerMwhUsd'],
  ['fuelCostPerMwhUsd', 'fuelCostPerMwhUsd'],
];

/**
 * Apply a scenario's RELATIVE adjustments to each phase.
 *
 * WHY: the phased engine prices per-phase CAPEX / CF / O&M, not the campus
 * rollup. A scenario that scaled only the campus fields therefore moved the
 * single-phase headline but left the PHASED headline — and the per-phase LCOE
 * exhibit — on the base numbers, so the report could show "Upside" economics
 * beside base per-phase rows that did not add up to them.
 *
 * ONLY `multiplier` and `delta` propagate. An `absolute` pin means "the PROJECT
 * total is exactly X"; copying X onto every phase would assert that each phase
 * costs the whole project, so an absolute adjustment is left at campus level
 * (the rollup then reconciles it on the next write).
 */
export function applyScenarioToPhases(
  phases: readonly FinancialPhase[],
  scenario: FinancialScenario | null | undefined,
): FinancialPhase[] {
  const adjustments = scenario?.adjustments;
  if (!adjustments || phases.length === 0) return [...phases];
  return phases.map((phase) => {
    // Collect the numeric overrides, then apply them in ONE typed spread —
    // every field in `PHASE_FIELD_FROM_CAMPUS` is `number | null | undefined`
    // on `FinancialPhase`, so the patch is assignable without a cast.
    const patch: Partial<Record<AdjustablePhaseField, number>> = {};
    for (const [phaseField, campusField] of PHASE_FIELD_FROM_CAMPUS) {
      const adj = adjustments[campusField];
      if (!adj || adj.kind === 'absolute') continue;
      const current = phase[phaseField];
      if (typeof current !== 'number' || !Number.isFinite(current)) continue;
      const raw = adj.kind === 'multiplier' ? current * adj.value : current + adj.value;
      patch[phaseField] = clampAdjusted(campusField, raw);
    }
    return { ...phase, ...patch };
  });
}

/**
 * Apply a scenario's adjustment for ONE campus field to a value that did not
 * come from the FA at all — e.g. a per-phase CAPEX derived from the Bill of
 * Materials.
 *
 * A scenario is a WHAT-IF over the whole project ("what if CAPEX were 10%
 * lower"), so it has to reach the number the engine actually spends, whatever
 * its source. Scaling only the FA-entered figures left the BoM-priced phases —
 * which are the AUTHORITATIVE CAPEX basis whenever a plan exists — on base
 * numbers, so an Upside case moved the single-phase headline and left the
 * phased one untouched.
 *
 * `absolute` does not propagate, for the same reason it does not propagate to
 * phases: pinning the PROJECT total says nothing about any one phase.
 */
export function adjustValueForScenario(
  value: number | null | undefined,
  field: ScenarioAdjustableField,
  scenario: FinancialScenario | null | undefined,
): number | null {
  if (value == null || !Number.isFinite(value)) return value ?? null;
  const adj = scenario?.adjustments?.[field];
  if (!adj || adj.kind === 'absolute') return value;
  const raw = adj.kind === 'multiplier' ? value * adj.value : value + adj.value;
  return clampAdjusted(field, raw);
}

/** The scenario the report and the engines run on, or null when none is marked. */
export function canonicalScenario(fa: FinancialAssumptions): FinancialScenario | null {
  return resolveCanonicalCore(fa).canonical;
}

/**
 * The canonical FA — core AND phases — for engines that read both.
 *
 * `canonicalCore` returns only the flat core, which is all the single-phase
 * engine needs. The PHASED engine reads `fa.phases`, so a caller that hands it
 * the raw phases beside a scenario-merged core is mixing two different cases.
 */
export function canonicalFinancialAssumptions(fa: FinancialAssumptions): FinancialAssumptions {
  const { core, canonical } = resolveCanonicalCore(fa);
  const phases = fa.phases ? applyScenarioToPhases(fa.phases, canonical) : fa.phases;
  return { ...fa, ...core, ...(phases ? { phases } : {}) };
}

/**
 * Resolve the canonical scenario's effective core — the one the LLM
 * sees via `buildFinancialAssumptionsBlock`.
 *
 * Returns the bare FA (no scenario applied) when NO scenario is marked
 * canonical, including when there are no scenarios at all.
 */
export function canonicalCore(fa: FinancialAssumptions): FinancialAssumptionsCore {
  return resolveCanonicalCore(fa).core;
}

/* ────────────────────────────────────────────────────────────────────────
 * Seeding
 * ──────────────────────────────────────────────────────────────────── */

/**
 * Seed default Base / Upside / Downside scenarios. Used when the user
 * opens the compare panel for the first time. The Base scenario is
 * marked canonical with an empty diff (== canonical core); Upside +
 * Downside swing CAPEX, capacity factor, and WACC — the three drivers
 * that historically dominate utility-scale project economics.
 *
 * Returns a NEW scenarios array; does not mutate the input FA.
 * Prefer `seedDefaultScenariosFor(base)`, which fills the Upside /
 * Downside adjustments.
 */
export function seedDefaultScenarios(): FinancialScenarioWithAdjustments[] {
  return [
    {
      id: 'base',
      label: 'Base',
      assumptions: {},
      isCanonical: true,
    },
    {
      id: 'upside',
      label: 'Upside',
      assumptions: {},   // adjustments populated by seedDefaultScenariosFor()
      isCanonical: false,
    },
    {
      id: 'downside',
      label: 'Downside',
      assumptions: {},
      isCanonical: false,
    },
  ];
}

/** Fields the default Upside / Downside swings touch, in display order. */
const SEEDED_DRIVERS: ScenarioAdjustableField[] = [
  'capexTotalUsd',
  'capacityFactorPct',
  'waccPct',
];

function filterToBase(
  adjustments: ScenarioAdjustments,
  base: FinancialAssumptionsCore | null | undefined,
): ScenarioAdjustments {
  if (!base) return adjustments;
  const out: ScenarioAdjustments = {};
  const numeric = base as Record<ScenarioAdjustableField, number | null>;
  for (const field of Object.keys(adjustments) as ScenarioAdjustableField[]) {
    const value = numeric[field];
    if (typeof value !== 'number' || !Number.isFinite(value)) continue;
    out[field] = adjustments[field];
  }
  return out;
}

/**
 * The conventional Upside swing: 10 % lower CAPEX, 5 pts higher capacity
 * factor (clamped at 100), 1 pt lower WACC (clamped at 1 %). Mirrors how
 * investment committees frame upside cases.
 *
 * RELATIVE by construction — re-derived against whatever the base CAPEX is
 * at merge time, so it cannot go stale (A9). When `base` is supplied, drivers
 * the base does not carry are omitted (an adjustment on an absent field is a
 * merge-time no-op anyway; omitting it keeps the scenario summary honest).
 */
export function upsideAdjustments(base?: FinancialAssumptionsCore | null): ScenarioAdjustments {
  return filterToBase({
    capexTotalUsd: { kind: 'multiplier', value: 0.9 },
    capacityFactorPct: { kind: 'delta', value: 5 },
    waccPct: { kind: 'delta', value: -1 },
  }, base);
}

/**
 * The conventional Downside swing: 15 % higher CAPEX, 7 pts lower capacity
 * factor (clamped at 1 %), 2 pts higher WACC (clamped at 30 %).
 */
export function downsideAdjustments(base?: FinancialAssumptionsCore | null): ScenarioAdjustments {
  return filterToBase({
    capexTotalUsd: { kind: 'multiplier', value: 1.15 },
    capacityFactorPct: { kind: 'delta', value: -7 },
    waccPct: { kind: 'delta', value: 2 },
  }, base);
}

/**
 * One-shot seeding: build the three default scenarios with the conventional
 * Upside / Downside adjustments pre-filled for the given base. Used by the
 * compare panel's "Seed defaults" CTA.
 */
export function seedDefaultScenariosFor(
  base: FinancialAssumptionsCore,
): FinancialScenarioWithAdjustments[] {
  const scenarios = seedDefaultScenarios();
  scenarios[1] = { ...scenarios[1], adjustments: upsideAdjustments(base) };
  scenarios[2] = { ...scenarios[2], adjustments: downsideAdjustments(base) };
  return scenarios;
}

/**
 * Set the canonical flag on exactly one scenario by id. Returns a new
 * scenarios array with `isCanonical` cleared on all others. Returns
 * the input unchanged when the id isn't found (no silent change).
 */
export function setCanonicalScenario(
  scenarios: FinancialScenario[],
  id: string,
): FinancialScenario[] {
  if (!scenarios.some(s => s.id === id)) return scenarios;
  return scenarios.map(s => ({ ...s, isCanonical: s.id === id }));
}

/* ────────────────────────────────────────────────────────────────────────
 * Summaries
 * ──────────────────────────────────────────────────────────────────── */

/**
 * Stable per-scenario human-readable summary for the compare card.
 * Lists the fields that differ from the base, with the new value.
 */
export function summariseDiff(diff: Partial<FinancialAssumptionsCore>): string {
  const parts = absoluteParts(diff);
  return parts.length === 0 ? 'no overrides' : parts.join(', ');
}

/**
 * Summary across BOTH channels — absolute overrides first, then the
 * relative adjustments (rendered as "−10 %" / "+5.0 pts", never as the
 * frozen number they resolve to today).
 */
export function summariseScenario(scenario: FinancialScenario): string {
  const parts = [
    ...absoluteParts(scenario.assumptions ?? {}),
    ...adjustmentParts(scenarioAdjustments(scenario)),
  ];
  return parts.length === 0 ? 'no overrides' : parts.join(', ');
}

function absoluteParts(diff: Partial<FinancialAssumptionsCore>): string[] {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(diff)) {
    if (v == null) continue;
    parts.push(`${k}=${formatScenarioValue(k, v as number | string)}`);
  }
  return parts;
}

function adjustmentParts(adjustments: ScenarioAdjustments): string[] {
  const parts: string[] = [];
  for (const field of Object.keys(adjustments) as ScenarioAdjustableField[]) {
    const adjustment = adjustments[field];
    if (!adjustment || !Number.isFinite(adjustment.value)) continue;
    parts.push(`${field} ${formatAdjustment(field, adjustment)}`);
  }
  return parts;
}

/** "-10%" / "+5.0 pts" / "= $2.00B" — the relative form, never the frozen number. */
export function formatAdjustment(field: string, adjustment: ScenarioAdjustment): string {
  const { kind, value } = adjustment;
  if (kind === 'absolute') return `= ${formatScenarioValue(field, value)}`;
  if (kind === 'multiplier') {
    const pct = (value - 1) * 100;
    return `${pct >= 0 ? '+' : '-'}${Math.abs(pct).toFixed(Math.abs(pct) < 1 ? 1 : 0)}%`;
  }
  const sign = value >= 0 ? '+' : '-';
  const magnitude = Math.abs(value);
  if (field.endsWith('Pct') || field.endsWith('PctPerYear')) {
    return `${sign}${magnitude.toFixed(1)} pts`;
  }
  return `${sign}${formatScenarioValue(field, magnitude)}`;
}

function formatScenarioValue(field: string, value: number | string): string {
  if (typeof value === 'string') return value;
  if (field.endsWith('Pct')) return `${value.toFixed(1)}%`;
  if (field === 'capexTotalUsd' || field === 'capexPerKwUsd' || field === 'decommissioningUsd' || field === 'workingCapitalUsd') {
    const abs = Math.abs(value);
    if (abs >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(2)}B`;
    if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `$${(value / 1_000).toFixed(1)}k`;
    return `$${value.toFixed(0)}`;
  }
  return String(value);
}
